/**
 * Phase 10 — Request Payment modal. Posts to /payment-requests, toasts the
 * resulting link, handles the SMS-skipped reason surfaced by the backend.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";

import {
  ErrorBanner,
  Field,
  Modal,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextArea,
  TextInput,
} from "../crm/Form";
import { createPaymentRequest } from "../../lib/paymentRequests";

const PAYMENT_TYPE_OPTIONS = [
  { value: "full", label: "Full Payment" },
  { value: "deposit", label: "Deposit" },
  { value: "custom", label: "Custom" },
];
const DELIVERY_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "both", label: "Both" },
];

export default function RequestPaymentModal({
  open,
  onClose,
  serviceRequest,
  onCreated,
}) {
  const [amountStr, setAmountStr] = useState("");
  const [paymentType, setPaymentType] = useState("full");
  const [description, setDescription] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("email");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open && serviceRequest) {
      setAmountStr("");
      setPaymentType("full");
      setDeliveryMethod("email");
      const addr = serviceRequest.property_address || serviceRequest.customer_name || "";
      setDescription(addr ? `Service call — ${addr}` : "Service call");
      setErr("");
      setBusy(false);
    }
  }, [open, serviceRequest]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const amountFloat = Number(amountStr);
    if (!Number.isFinite(amountFloat) || amountFloat < 1) {
      setErr("Amount must be at least $1.00");
      return;
    }
    const amountCents = Math.round(amountFloat * 100);
    if (!description.trim()) {
      setErr("Description is required");
      return;
    }
    setBusy(true);
    try {
      const resp = await createPaymentRequest({
        service_request_id: serviceRequest.id,
        amount_cents: amountCents,
        payment_type: paymentType,
        description: description.trim().slice(0, 500),
        delivery_method: deliveryMethod,
      });
      toast.success(
        `Payment request sent · ${resp.payment_request.stripe_payment_link_url.slice(0, 40)}…`
      );
      if (resp.sms_skipped) {
        const reason =
          resp.sms_skipped_reason === "no_consent"
            ? "Customer has not opted in to SMS"
            : "Customer has no phone number on file";
        toast.warning(`SMS not sent: ${reason}`);
      }
      onCreated(resp.payment_request);
      onClose();
    } catch (e2) {
      setErr(
        e2?.response?.data?.detail ||
          e2?.message ||
          "Could not create payment request"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Request Payment"
      size="md"
      data-testid="request-payment-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <ErrorBanner message={err} />
        <Field label="Amount (USD)" required>
          <TextInput
            value={amountStr}
            onChange={setAmountStr}
            placeholder="150.00"
            type="number"
            step="0.01"
            min="1"
            data-testid="request-payment-amount"
          />
        </Field>
        <Field label="Payment type" required>
          <Select
            value={paymentType}
            onChange={setPaymentType}
            options={PAYMENT_TYPE_OPTIONS}
            data-testid="request-payment-type"
          />
        </Field>
        <Field label="Description (shown on Stripe page)" required>
          <TextArea
            value={description}
            onChange={setDescription}
            rows={3}
            maxLength={500}
            data-testid="request-payment-description"
          />
          <div className="text-[11px] text-slate-500 mt-1">
            {description.length} / 500
          </div>
        </Field>
        <Field label="Delivery method" required>
          <Select
            value={deliveryMethod}
            onChange={setDeliveryMethod}
            options={DELIVERY_OPTIONS}
            data-testid="request-payment-delivery"
          />
        </Field>
        <div className="flex items-center justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose} disabled={busy}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="request-payment-submit"
          >
            {busy ? "Sending…" : "Create & send"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}
